import { getCartErrorMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      this.#submit(event.target, event.submitter);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const line = this.#lineFromFormData(new FormData(form));
    if (!line) {
      this.#abortController = null;
      return;
    }

    // Merchant request: a ticked gift-wrap box also adds the wrapping product, one per wrapped piece.
    // 2026-11-03: the line keeps how many pieces are wrapped (ギフトラッピング（個数）: n), so the box's value becomes the quantity.
    const lines = [line];
    const giftWrap = form.querySelector('[data-gift-wrap-variant]');
    if (giftWrap instanceof HTMLInputElement && giftWrap.checked && giftWrap.dataset.giftWrapVariant) {
      const wrapKey = (giftWrap.name.match(/^properties\[(.*)\]$/) || [])[1];
      if (wrapKey && Array.isArray(line.attributes)) {
        line.attributes = line.attributes.map((attribute) => attribute.key === wrapKey ? { key: wrapKey, value: String(line.quantity) } : attribute);
        line.wrapKey = wrapKey;
      }
      lines.push({ merchandiseId: giftWrap.dataset.giftWrapVariant, quantity: line.quantity });
    }

    this.#setSubmitting(true, submitter);
    this.#showError('');

    try {
      // Merchant request 2026-11-03: products were not reaching the cart page on the live store, so adds go
      // straight to the classic cart endpoint (the same cart the cart page reads) instead of updateCart.
      const result = await this.#addToCart(form, lines, signal);

      this.#throwIfAborted(signal);

      try {
        await customElements.get('s-cart')?.refreshMountedCarts?.({ signal });
      } catch (error) {
        // The add already succeeded; a stale cart count is not an error for the buyer.
      }

      const errorMessage = this.#resultErrorMessage(result);
      if (errorMessage) {
        this.#showError(errorMessage);
      } else {
        if (window.Shopify?.actions?.openCart) await window.Shopify.actions.openCart();
        else window.location.assign('/cart');
      }
    } catch (error) {
      if (signal.aborted) return;

      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  async #addToCart(form, lines, signal) {
    const toId = (merchandiseId) => Number(String(merchandiseId).split('/').pop());

    // 2026-11-03: when the same product is already in the cart with wrapping, add to that line and raise its wrapped
    // count, instead of letting the cart merge the lines and lose wrapped pieces.
    const wrappedLine = lines.find((line) => line.wrapKey);
    if (wrappedLine) {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, signal })).json();
        const variantId = toId(wrappedLine.merchandiseId);
        const existing = cart.items.find((item) => item.variant_id === variantId && item.properties && item.properties[wrappedLine.wrapKey]);
        if (existing) {
          const count = Math.min(parseInt(existing.properties[wrappedLine.wrapKey], 10) || 0, existing.quantity);
          // In-place property changes are not reliable, so the line is replaced.
          const post = (path, body) => fetch(`${root}${path}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
            signal,
          });
          // Take the old line out first (so low stock is not counted twice), then add the combined pieces; if that
          // fails, put the old line back as it was.
          const removed = await post('cart/change.js', { id: existing.key, quantity: 0 });
          if (removed.ok) {
            const added = await post('cart/add.js', {
              items: [{
                id: variantId,
                quantity: existing.quantity + wrappedLine.quantity,
                properties: { ...existing.properties, [wrappedLine.wrapKey]: String(count + wrappedLine.quantity) },
              }],
            });
            if (added.ok) {
              lines = lines.filter((line) => line !== wrappedLine);
            } else {
              await post('cart/add.js', { items: [{ id: variantId, quantity: existing.quantity, properties: existing.properties }] });
            }
          }
        }
      } catch (error) {
        if (signal?.aborted) throw error;
        // Fall back to a normal add below.
      }
    }
    const items = lines.map((line) => {
      const item = { id: toId(line.merchandiseId), quantity: line.quantity };
      if (line.sellingPlanId) item.selling_plan = toId(line.sellingPlanId);
      if (line.attributes?.length) {
        item.properties = Object.fromEntries(line.attributes.map((attribute) => [attribute.key, attribute.value]));
      }
      return item;
    });

    const action = form.getAttribute('action') || '/cart/add';
    const response = await fetch(`${action.replace(/\.js$/, '')}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
      signal,
    });

    if (response.ok) return {};

    let message = '';
    try {
      const body = await response.json();
      message = body?.description || body?.message || '';
    } catch (error) {
      // Use the generic message below.
    }
    return { userErrors: [{ message: message || this.dataset.errorMessage || '' }] };
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('opacity-50', 'cursor-wait');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
